/**
 * @format
 * @module lib/global/variant-tracker
 * @description Check for HTML that indicates this section page is a control/variant page, so then
 * we should fire an analytics event to record the user page view.
 */
import tgamConfig from "config/tgam";
import logger from "utils/logger";
import { getUserRole } from "lib/identity/api";
import { getProp, getById } from "utils/utils";

const debugTag = "[VARIANT-TRACKER]";

export default function variantTracker() {
  const pbAdmin = getProp(tgamConfig, "env.isPbAdmin", false);
  if (pbAdmin) {
    return;
  }

  // HTML in: features/optimizely/tracker/feature.jsp
  const tracker = getById("js-cdn-tracker");
  if (!tracker) {
    return;
  }

  const { id: testId, name: variantName } = tracker.dataset;
  if (testId && variantName) {
    sendSuccess(testId, variantName);
  }
}

/**
 * Create and send a success event
 * @param {String} testId - or experiment id/key
 * @param {String} variant - test variant name
 */
function sendSuccess(testId, variant) {
  const event = createEvent({
    testId, // e.g. "pb_classic_section_cdn_123"
    variant, // e.g. "control", "control_1", "control_2", "variant_1", "variant_2"
    engine: "akamai", // The CDN, instead of "optimizely" or "mediator"
    segment: getUserRole().name, // "Anonymous", "Registered", or "Subscribed".
    store: "visitor"
  });
  logger.log(`${debugTag} event:`, event);
  sendEvent(event).catch(console.error);
}

/**
 * Send Sophi general event
 * @param {Object} c - collection
 * @param {String} c.testId - or experiment id/key
 * @param {String} c.engine - Who is performing the user bucketing. e.g. "akamai"
 * @param {String} c.segment - audience segment name
 * @param {String} c.variant - test variant name
 * @param {String} c.store - store event type. e.g. "page", "visitor"
 * @returns {Object}
 */
function createEvent({ testId, engine, segment, variant, store }) {
  return {
    type: "ab_tests",
    data: {
      tests: [
        {
          testId,
          engine,
          variantName: variant,
          segmentName: segment,
          store
        }
      ]
    }
  };
}

/**
 * Send Sophi general event, with callback timeout limit.
 * Only `window.sophi.data.environment.trackerVersion` >= 2.1.0 supports the callback property method.
 * @param {Object} event - analytics data payload to send
 * @param {Number} [ms=3000] - the timeout in milliseconds
 * @returns {Promise<Boolean|undefined>}
 */
function sendEvent(event, ms = 3000) {
  if (window.sophi && window.sophi.sendEvent) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        reject(new Error(`Sophi.sendEvent() timeout exceeded: ${ms}ms.`));
      }, ms);
      // Resolve the Promise once async sendEvent() is done it's task.
      // We'll be notified via config.callback method, which is also the Promise's resolve.
      const config = { callback: resolve };
      window.sophi.sendEvent({ ...event, config });
    });
  }
  return Promise.reject(new Error("Sophi.sendEvent() failed to send"));
}
